import type { FeedbackAnswers, FeedbackQuestion } from './transcript-feedback.model';

/**
 * Pure step logic for the transcript feedback bar.
 * Questions are a server-driven draft: never name a question or option key here.
 */

/**
 * A question is visible when it has no `showIf`, or its parent is itself visible
 * AND the parent's answers contain any of `showIf.optionKeys`. The parent check is
 * recursive so stale answers of a hidden parent cannot reveal its children.
 */
function visibleKeySet(questions: FeedbackQuestion[], answers: FeedbackAnswers): Set<string> {
  const byKey = new Map(questions.map((q) => [q.key, q]));
  const memo = new Map<string, boolean>();

  const isVisible = (q: FeedbackQuestion, seen: Set<string>): boolean => {
    const cached = memo.get(q.key);
    if (cached !== undefined) return cached;
    let result: boolean;
    if (!q.showIf) {
      result = true;
    } else if (seen.has(q.key)) {
      result = false; // cycle guard
    } else {
      const parent = byKey.get(q.showIf.questionKey);
      const given = answers[q.showIf.questionKey] ?? [];
      result =
        !!parent &&
        isVisible(parent, new Set(seen).add(q.key)) &&
        q.showIf.optionKeys.some((k) => given.includes(k));
    }
    memo.set(q.key, result);
    return result;
  };

  return new Set(questions.filter((q) => isVisible(q, new Set())).map((q) => q.key));
}

export function visibleQuestions(
  questions: FeedbackQuestion[],
  answers: FeedbackAnswers
): FeedbackQuestion[] {
  const keys = visibleKeySet(questions, answers);
  return questions.filter((q) => keys.has(q.key));
}

/** Sorted unique steps of visible questions; `[1]` when nothing is visible. */
export function visibleSteps(questions: FeedbackQuestion[], answers: FeedbackAnswers): number[] {
  const steps = [...new Set(visibleQuestions(questions, answers).map((q) => q.step))];
  return steps.length ? steps.sort((a, b) => a - b) : [1];
}

/** Every visible question of `step` has at least one answer. */
export function isStepComplete(
  questions: FeedbackQuestion[],
  answers: FeedbackAnswers,
  step: number
): boolean {
  return visibleQuestions(questions, answers)
    .filter((q) => q.step === step)
    .every((q) => (answers[q.key]?.length ?? 0) > 0);
}

/** Removes answers of questions that are not visible. */
export function pruneAnswers(
  questions: FeedbackQuestion[],
  answers: FeedbackAnswers
): FeedbackAnswers {
  const keys = visibleKeySet(questions, answers);
  return Object.fromEntries(Object.entries(answers).filter(([k]) => keys.has(k)));
}

/**
 * Keeps only the questions and options the current questionnaire still has. A saved
 * answer may use an older version; an option that no longer exists cannot be unticked
 * in the form, and aida rejects it (422).
 */
export function keepKnownAnswers(
  questions: FeedbackQuestion[],
  answers: FeedbackAnswers
): FeedbackAnswers {
  const known: FeedbackAnswers = {};
  for (const q of questions) {
    const kept = (answers[q.key] ?? []).filter((k) => q.options.some((o) => o.key === k));
    if (kept.length) known[q.key] = kept;
  }
  return known;
}

/** Labels of the first question's answer, then all visible `checkboxes` answers. */
export function summarize(questions: FeedbackQuestion[], answers: FeedbackAnswers): string {
  const visible = visibleQuestions(questions, answers);
  // A saved answer can name an option a later questionnaire removed; skip it rather than show its key.
  const labelsOf = (q: FeedbackQuestion): string[] =>
    (answers[q.key] ?? []).flatMap((k) => {
      const label = q.options.find((o) => o.key === k)?.label.en;
      return label ? [label] : [];
    });

  const first = questions[0];
  const parts: string[] = first ? labelsOf(first) : [];
  for (const q of visible) {
    if (q.type === 'checkboxes' && q !== first) parts.push(...labelsOf(q));
  }
  return parts.join(' · ');
}
